import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { hasConsentChoice, setConsent } from '../../utils/cookieConsent';
import CookieSettings from './CookieSettings';

// Nelle app native (iOS + Android) NON mostriamo il banner cookie e non attiviamo
// GA4: il consenso resta in default-deny (vedi public/index.html), quindi nessun
// cookie analitico viene scritto e non c'è alcun tracciamento. Requisito Apple
// 5.1.2(i): un prompt cookie nell'app implicherebbe ATT. Sul web il banner e GA4
// restano invariati.
const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Banner cookie bottom-sheet (GDPR / nLPD).
 *
 * Mostrato al primo accesso se l'utente non ha mai espresso una scelta
 * (chiave `ticino-wild-cookie-consent` assente o versione obsoleta).
 *
 * Tre azioni:
 *  - "Accetta tutti"        → analytics = true
 *  - "Rifiuta non essenziali" → analytics = false
 *  - "Personalizza"         → apre <CookieSettings />
 *
 * Una volta espressa la scelta (qualsiasi), il banner si chiude e non viene
 * più mostrato. L'utente può revocare in qualunque momento dal footer
 * (entry-point su `CookieSettings`) o dal proprio profilo.
 */
const CookieBanner = () => {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Decidi se mostrare solo al mount client-side (evita flash in SSG).
    // Su app nativa il banner non si mostra mai (vedi IS_NATIVE).
    useEffect(() => {
        if (IS_NATIVE) return;
        if (!hasConsentChoice()) setVisible(true);
    }, []);

    const handleAcceptAll = () => {
        setConsent({ analytics: true });
        setVisible(false);
    };

    const handleRejectAll = () => {
        setConsent({ analytics: false });
        setVisible(false);
    };

    const handleCustomize = () => {
        setSettingsOpen(true);
    };

    const handleSettingsClose = () => {
        setSettingsOpen(false);
        // Dopo aver salvato dalle Settings, hasConsentChoice() torna true → nasconde il banner
        if (hasConsentChoice()) setVisible(false);
    };

    // App nativa: nessun banner, nessuna gestione cookie (no GA4 → no tracking).
    if (IS_NATIVE) return null;

    if (!visible) {
        // Le Settings restano montabili anche senza banner (entry-point esterno)
        return <CookieSettings isOpen={settingsOpen} onClose={handleSettingsClose} />;
    }

    return (
        <>
            <div
                className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-neutral-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 sm:px-5 pt-4 sm:pt-5 pb-[max(env(safe-area-inset-bottom),1rem)]"
                role="dialog"
                aria-modal="false"
                aria-labelledby="cookie-banner-title"
            >
                <div className="max-w-5xl mx-auto flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
                    <div className="flex-1">
                        <h2 id="cookie-banner-title" className="font-semibold text-[#225424] mb-1">
                            {t('CookieBanner.title')}
                        </h2>
                        <p className="text-sm text-neutral-700 leading-snug">
                            {t('CookieBanner.message')}{' '}
                            <Link to="/privacy-policy" className="underline text-[#225424]">
                                Privacy Policy
                            </Link>.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
                        <button
                            type="button"
                            className="secondary text-sm whitespace-nowrap"
                            onClick={handleRejectAll}
                        >
                            {t('CookieBanner.rejectAll')}
                        </button>
                        <button
                            type="button"
                            className="secondary text-sm whitespace-nowrap"
                            onClick={handleCustomize}
                        >
                            {t('CookieBanner.customize')}
                        </button>
                        <button
                            type="button"
                            className="primary text-sm whitespace-nowrap"
                            onClick={handleAcceptAll}
                        >
                            {t('CookieBanner.acceptAll')}
                        </button>
                    </div>
                </div>
            </div>
            <CookieSettings isOpen={settingsOpen} onClose={handleSettingsClose} />
        </>
    );
};

export default CookieBanner;
